import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";

/**
 * Controlled autocomplete.
 *
 * Every consumer derives `field.value` from a watched form value and maps the selection
 * back to a primitive in `field.onChange`, so this has always been a controlled component.
 * It used to also mirror `value` into local state and push that copy back out from an
 * effect, which caused two distinct failures:
 *
 *  - The copy was seeded once and never resynced, so a `value` that resolved later (any
 *    option list fetched asynchronously) never showed up as the selection.
 *  - The effect ran on mount and wrote the copy into the form. When the options hadn't
 *    loaded yet the copy was `undefined`, so simply opening a form and saving cleared a
 *    team's conference and state — and it is how a stored venue timezone was replaced by
 *    the admin's browser zone.
 *
 * Rendering straight from props removes both, and drops the exhaustive-deps warning that
 * flagged the write-back in the first place.
 */
const AutocompleteField = ({ data, field, label, size, isDisabled }) => {
  const { name, value, onChange, required, ...other } = field;

  // Consumers rebuild `onChange` inline on every render, so it cannot be an effect
  // dependency without re-firing forever. Read the latest one through a ref instead.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Clearing the selection when the field becomes disabled is deliberate — e.g. conference
  // only applies to NCAA teams. This fires on the transition, not on every render.
  useEffect(() => {
    if (isDisabled) onChangeRef.current(null);
  }, [isDisabled]);

  const getOptionLabel = (option) => option?.label || "";

  const getOptionSelected = (option, selectedOption) =>
    option?.id === selectedOption?.id;

  const renderOption = (option) => (
    <>
      {option.iconBefore}
      {option.label}
    </>
  );

  // `undefined` would make MUI treat the input as uncontrolled and warn.
  const selected = value ?? null;

  return (
    <Autocomplete
      disablePortal
      id={name}
      getOptionLabel={getOptionLabel}
      getOptionSelected={getOptionSelected}
      renderOption={renderOption}
      options={data}
      value={selected}
      onChange={(event, newValue) => onChange(newValue)}
      disabled={isDisabled}
      renderInput={(params) => (
        <TextField
          {...params}
          autoComplete={name}
          label={label}
          variant="outlined"
          fullWidth
          size={size}
          required={required}
          InputProps={{
            ...params.InputProps,
            startAdornment: selected?.iconBefore
              ? selected.iconBefore
              : params.InputProps.startAdornment,
          }}
        />
      )}
      name={name}
      size={size}
      {...other}
    />
  );
};

AutocompleteField.propTypes = {
  data: PropTypes.array.isRequired,
  field: PropTypes.object,
  label: PropTypes.string,
  size: PropTypes.string,
  title: PropTypes.string,
  isDisabled: PropTypes.bool,
};

export default AutocompleteField;
